"use client"

import { Suspense, type ComponentProps } from "react"
import clsx from "clsx"

import { SORT_BY } from "~/lib/algolia/constants"
import type { SortBy } from "~/lib/algolia/types"
import Select from "~/components/abstracts/Select"
import { useAlgoliaCustomContext } from "~/providers/AlgoliaCustomContext/index.client"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"

import * as css from "./styles.css"

function SortBySelectFallback({ onChange, value }: Pick<ComponentProps<typeof Select>, "onChange" | "value">) {
  const t = useTranslate()

  return (
    <Select
      className={clsx(css.select)}
      selectClassName={css.selectItem}
      aria-label={t("aria_select_sorting")}
      onChange={onChange}
      value={value ?? "default"}
      options={[
        { label: t("sort_by_new"), value: "default" },
        { label: t("sort_by_price_asc"), value: SORT_BY.PRICE_ASC.SUFFIX },
        { label: t("sort_by_price_desc"), value: SORT_BY.PRICE_DESC.SUFFIX },
      ]}
    />
  )
}

function SortBySelectSuspense() {
  const { sortBy, setSortBy } = useAlgoliaCustomContext()

  return (
    <SortBySelectFallback
      onChange={(e) => {
        const newSortBy = e.target.value as SortBy

        if (!newSortBy) return

        if (newSortBy === "default") {
          setSortBy?.(null)
          return
        }

        setSortBy?.(newSortBy)
      }}
      value={sortBy ?? "default"}
    />
  )
}

export function AlgoliaSortBy() {
  return (
    <Suspense fallback={<SortBySelectFallback />}>
      <SortBySelectSuspense />
    </Suspense>
  )
}
